import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CirclePlus, Pencil } from "lucide-react";
import { DeleteBtn } from "@/components/Delete";
import { LoaderSpin } from "@/components/Loaders";
import { useToastNavigate } from "@/utils/toastNavigate";
import { getSelectPositions } from "@/Api/positions";
import {
  getEmployeePositionHistories,
  createEmployeePositionHistory,
  updateEmployeePositionHistory,
  deleteEmployeePositionHistory,
} from "@/Api/employeePositionHistories";

const schema = z.object({
  position_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
  cbo_code: z.string().optional(),
  effective_date: z.string({ message: "*requerido" }).min(1, "*requerido"),
  notes: z.string().optional(),
});

type SchemaType = z.infer<typeof schema>;

const formatDate = (value?: string | null) =>
  value ? value.slice(0, 10).split("-").reverse().join("/") : "-";

const HistoryForm = ({
  employeeId,
  unityId,
  editing,
  onSaved,
}: {
  employeeId: number;
  unityId?: number | string | null;
  editing?: any;
  onSaved: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState("default");
  const [positions, setPositions] = useState([]);
  const { toast } = useToastNavigate();

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      position_id: editing?.position_id ? String(editing.position_id) : "",
      cbo_code: editing?.cbo_code || "",
      effective_date: editing?.effective_date?.slice(0, 10) || "",
      notes: editing?.notes || "",
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    getSelectPositions(unityId ?? undefined).then(({ data }) =>
      setPositions(data?.response || [])
    );
  }, [isOpen, unityId]);

  const onSubmit = async (data: SchemaType) => {
    setState("loading");
    const payload = editing
      ? data
      : { ...data, employee_id: employeeId };

    const { data: apiResponse, ok } = editing
      ? await updateEmployeePositionHistory(editing.uuid, payload)
      : await createEmployeePositionHistory(payload);

    if (!ok) {
      setState("error");
      toast({
        description: apiResponse?.message || "Erro ao salvar",
        variant: "destructive",
      });
      setTimeout(() => setState("default"), 2000);
      return;
    }

    setState("default");
    setIsOpen(false);
    form.reset();
    toast({ description: "Salvo com sucesso!" });
    onSaved();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {editing ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          >
            <Pencil className="size-5" />
          </Button>
        ) : (
          <Button type="button" size="sm" variant="outline" className="gap-1">
            <CirclePlus className="size-4" />
            Novo Cargo
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogDescription className="text-lg">
            {editing ? "Editar Cargo" : "Novo Registro de Cargo"}
          </DialogDescription>
        </DialogHeader>
        <Form onSubmit={form.handleSubmit(onSubmit)} {...form} className="space-y-3">
          <Form.Row>
            <Form.Field name="position_id">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Cargo <Form.Message />
                  </Form.Label>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {positions.map((position: { id: string; name: string }) => (
                        <SelectItem key={position.id} value={String(position.id)}>
                          {position.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>
          <Form.Row>
            <Form.Field name="cbo_code">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>CBO</Form.Label>
                  <Form.Control>
                    <Input {...field} placeholder="Ex: 413105" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
            <Form.Field name="effective_date">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Data <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} type="date" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>
          <Form.Row>
            <Form.Field name="notes">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>Observações</Form.Label>
                  <Form.Control>
                    <Textarea {...field} placeholder="Opcional" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>
          <DialogFooter>
            <Button type="submit" className="w-full" disabled={state !== "default"}>
              {state === "loading" ? <LoaderSpin /> : "Salvar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export const PositionHistorySection = ({
  employeeId,
  unityId,
}: {
  employeeId: number;
  unityId?: number | string | null;
}) => {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    const { data } = await getEmployeePositionHistories(employeeId);
    setHistory(data?.response || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Histórico de Cargos e Funções</label>
        <HistoryForm employeeId={employeeId} unityId={unityId} onSaved={fetchData} />
      </div>
      {!loading && (
        <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
          <TableHeader>
            <TableRow>
              <TableHead>Cargo</TableHead>
              <TableHead>CBO</TableHead>
              <TableHead>Data</TableHead>
              <TableHead className="text-center">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.map((item) => (
              <TableRow key={item.uuid}>
                <TableCell>{item.position?.name}</TableCell>
                <TableCell>{item.cbo_code || "-"}</TableCell>
                <TableCell>{formatDate(item.effective_date)}</TableCell>
                <TableCell className="flex justify-center gap-1">
                  <HistoryForm employeeId={employeeId} unityId={unityId} editing={item} onSaved={fetchData} />
                  <DeleteBtn
                    params={{ func: deleteEmployeePositionHistory, funcParams: item.uuid }}
                    updateFront={fetchData}
                    referenceLabel={item.position?.name}
                  />
                </TableCell>
              </TableRow>
            ))}
            {history.length < 1 && (
              <TableRow>
                <TableCell colSpan={4}>Nada foi encontrado.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
